import React, { useState } from "react";
import { motion } from "framer-motion";
import { ClipboardList, Megaphone, Target, User, ShieldCheck, CreditCard } from "lucide-react";

const sections = [
  {
    id: "provision",
    title: "1. Provision of Information",
    icon: ClipboardList,
    content: (
      <>
        <p className="mb-4">
          247 Comcare Limited is committed to providing clear, transparent, and accurate information to all subscribers across its Value-Added Services (VAS).
        </p>
        <p className="mb-4">
          All service details, including pricing, subscription duration, opt-in/opt-out procedures, and customer support will be provided through:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>SMS notifications</li>
          <li>USSD menus</li>
          <li>IVR prompts</li>
          <li>Web/WAP pages</li>
          <li>Customer care channels</li>
          <li>Approved marketing materials</li>
        </ul>
        <p className="text-sm italic text-gray-600 mb-8">
          All information will comply with NCC Consumer Code of Practice Regulations, VAS Guidelines, and all applicable regulatory directives.
        </p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">a. Description of Services</h4>
        <p className="mb-4">
          247 Comcare offers a portfolio of mobile VAS designed to enhance digital experiences for subscribers across mobile networks in Nigeria. Services include:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-3">
          <li>
            <strong>Interactive Trivia & Gaming Services</strong>
            <p className="text-gray-600">(Football Zone, Music Trivia, WordPlay, educational quizzes, skill-based games)</p>
          </li>
          <li>
            <strong>Mobile Content Subscription Services</strong>
            <p className="text-gray-600">(Entertainment, lifestyle, motivational content, tips, devotionals, educational content)</p>
          </li>
          <li>
            <strong>Alerts & Information Services</strong>
            <p className="text-gray-600">(Fact-of-the-day, health tips, productivity messages, inspirational messages)</p>
          </li>
          <li>
            <strong>Digital Learning & Micro-Training Content</strong>
          </li>
        </ul>
        <p className="text-sm italic text-gray-600 mb-8">
          All services are delivered using approved shortcodes and in partnership with licensed aggregators and Mobile Network Operators (MNOs).
        </p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">b. Service Contract, Duration & Pricing Structure</h4>
        <p className="mb-4">
          247 Comcare provides subscription-based and on-demand services with clearly defined service durations.
        </p>
        <p className="mb-6">
          All subscriptions are <strong>fixed-duration</strong> and <strong>terminate automatically</strong> once the duration elapses. Users may subscribe again if they wish to continue service access.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100">
            <h5 className="font-bold text-blue-900 mb-3">1. Daily Subscription Plans</h5>
            <ul className="space-y-2 text-sm">
              <li>✓ Valid for <strong>24 hours</strong></li>
              <li>✓ Renews next day, except subscription is one off</li>
              <li>✓ Price Range: <strong>₦100 – ₦200</strong></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100">
            <h5 className="font-bold text-blue-900 mb-3">2. Weekly Subscription Plans</h5>
            <ul className="space-y-2 text-sm">
              <li>✓ Valid for <strong>7 days</strong></li>
              <li>✓ Renews, except subscription is one off</li>
              <li>✓ Price Range: <strong>₦200 – ₦500</strong></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100">
            <h5 className="font-bold text-blue-900 mb-3">3. Monthly Subscription Plans</h5>
            <ul className="space-y-2 text-sm">
              <li>✓ Valid for <strong>30 days</strong></li>
              <li>✓ Renews, except subscription is one off</li>
              <li>✓ Price Range: <strong>₦500 – ₦2,000</strong></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100">
            <h5 className="font-bold text-blue-900 mb-3">4. On-Demand (One-Time Access)</h5>
            <ul className="space-y-2 text-sm">
              <li>✓ Single-use access to content or gameplay</li>
              <li>✓ No continuation unless the user subscribes again</li>
              <li>✓ Price Range: <strong>₦50 or ₦100</strong></li>
            </ul>
          </div>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-8">
          <h5 className="font-bold text-yellow-800 mb-2">Pricing Disclosure</h5>
          <p className="text-sm mb-2">All pricing information will be communicated:</p>
          <ul className="list-disc pl-6 text-sm space-y-1">
            <li>Before subscription</li>
            <li>In Double Opt-In (DOI) messages</li>
            <li>Through USSD/Web/IVR menus</li>
            <li>In welcome and confirmation messages</li>
          </ul>
        </div>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">c. Service Subscription</h4>
        <p className="mb-4">
          247 Comcare complies fully with NCC's Double Opt-In (DOI) requirement for all subscription services.
        </p>
        <p className="mb-6">
          All subscriptions begin <strong>only</strong> after the user clearly confirms interest and acknowledges the service price and duration.
        </p>

        <h5 className="font-bold text-gray-800 mb-4">Subscription Process</h5>
        <p className="mb-4">Subscribers may opt in through <strong>SMS, USSD, IVR, or Web/WAP</strong> as follows:</p>

        <div className="space-y-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
            <h6 className="font-bold text-blue-900">SMS:</h6>
            <p className="text-sm text-gray-600">
              User sends keyword → receives DOI message (showing service details) → confirms by replying "YES" → receives welcome message.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
            <h6 className="font-bold text-green-900">USSD:</h6>
            <p className="text-sm text-gray-600">
              User dials USSD code → views service information and pricing → selects "Confirm/Subscribe" → receives confirmation SMS.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
            <h6 className="font-bold text-purple-900">IVR:</h6>
            <p className="text-sm text-gray-600">
              Pricing and service details are played → user confirms by pressing the designated key → receives confirmation SMS.
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-orange-500">
            <h6 className="font-bold text-orange-900">Web/WAP:</h6>
            <p className="text-sm text-gray-600">
              Page displays the full service information → user clicks "Subscribe/Confirm" → receives confirmation message after OTP validation.
            </p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-8">
          <p className="text-red-800 font-semibold text-center">
            ⚠️ No subscription is activated without the subscriber's explicit confirmation.
          </p>
        </div>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">d. Contract Terms & Conditions</h4>
        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <span className="text-blue-500 mr-3">✓</span>
            <span>Subscribers will receive clear information on service nature, pricing, and duration before opting in.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3">✓</span>
            <span>All services run for fixed periods and terminate automatically.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3">✓</span>
            <span>No user will ever be subscribed without explicit DOI confirmation.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3">✓</span>
            <span>All billing is communicated transparently and in line with NCC standards.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3">✓</span>
            <span>247 Comcare will ensure full compliance with NDPR and NCC data privacy and protection requirements.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3">✓</span>
            <span>Complaints will be handled promptly following NCC Consumer Code of Practice timelines.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3">✓</span>
            <span>247 Comcare reserves the right to modify or suspend services based on regulatory directives or network conditions but will always inform subscribers accordingly.</span>
          </li>
        </ul>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">e. Opt-In and Opt-Out Procedures</h4>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-green-50 p-6 rounded-xl border border-green-200">
            <h5 className="font-bold text-green-800 mb-4 text-lg">Opt-In</h5>
            <p className="text-sm mb-3">Users may opt in through SMS, USSD, IVR, or WAP/Web.</p>
            <p className="text-sm font-semibold text-green-700">
              All opt-ins require mandatory Double Opt-In confirmation.
            </p>
          </div>

          <div className="bg-red-50 p-6 rounded-xl border border-red-200">
            <h5 className="font-bold text-red-800 mb-4 text-lg">Opt-Out</h5>
            <p className="text-sm mb-3">247 Comcare ensures a seamless and always-available opt-out process via:</p>
            <ul className="text-sm space-y-2">
              <li><strong>SMS:</strong> Sending "STOP" or service-specific opt-out keyword to the shortcode</li>
              <li><strong>USSD:</strong> Dialing the service's opt-out code or dial *305#</li>
              <li><strong>IVR:</strong> Selecting the unsubscribe option</li>
              <li><strong>MNO Self-Care Portal</strong></li>
              <li><strong>Customer Care Escalation</strong></li>
            </ul>
          </div>
        </div>

        <div className="bg-gray-50 p-5 rounded-xl">
          <h5 className="font-bold text-gray-800 mb-3">Upon opting out:</h5>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Access to the service stops</li>
            <li>No further billing occurs</li>
            <li>Confirmation SMS is immediately sent to the user</li>
          </ol>
        </div>
      </>
    ),
  },
  {
    id: "advertisement",
    title: "2. Advertisement and Representation of Services",
    icon: Megaphone,
    content: (
      <>
        <p className="mb-6">
          247 Comcare ensures that all service promotions, representations, and communications are truthful, accurate, and compliant with NCC's Consumer Code of Practice and VAS Advertising Guidelines. All advertisements will clearly state the nature of the service, applicable pricing, and subscription requirements.
        </p>

        <h4 className="text-xl font-semibold mb-3 text-blue-900">a. Availability of Service</h4>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>All advertised services will be <strong>available</strong>, functional, and accessible through approved channels (SMS, USSD, IVR, Web/WAP) at the time of promotion.</li>
          <li>If a service becomes temporarily unavailable due to network, system maintenance, or regulatory reasons, subscribers will be informed accordingly.</li>
          <li>247 Comcare will not promote or market any service that is not live, approved, or ready for end-user access.</li>
        </ul>

        <h4 className="text-xl font-semibold mb-3 text-blue-900">b. Advertisement of Packaged Services</h4>
        <p className="mb-3">Where services are offered as <strong>bundles or packaged content</strong>, the advertisement will clearly state:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>The type of services included</li>
          <li>The validity period</li>
          <li>The subscription price</li>
          <li>Any usage limitations</li>
        </ul>
        <p className="text-sm text-gray-600 mb-8">
          Packages will not contain misleading claims or hidden charges. All bundled service promotions will follow NCC pricing transparency rules.
        </p>

        <h4 className="text-xl font-semibold mb-3 text-blue-900">c. Disclaimer</h4>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>247 Comcare will include appropriate <strong>disclaimers</strong> in all service communications and advertisements.</li>
          <li>Disclaimers will clarify:
            <ul className="list-circle pl-6 mt-2 space-y-1">
              <li>That subscription charges apply only after the subscriber's confirmation</li>
              <li>That service content is for informational/entertainment purposes</li>
              <li>That network availability may affect delivery time or continuity</li>
            </ul>
          </li>
          <li>Disclaimers will be written in clear and simple language as required by NCC.</li>
        </ul>

        <h4 className="text-xl font-semibold mb-3 text-blue-900">d. Tele-Marketing</h4>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Any tele-marketing activity (SMS campaigns, outbound dialling, IVR calls, or promotional messages) will strictly comply with:
            <ul className="list-circle pl-6 mt-2 space-y-1">
              <li>NCC Consumer Code of Practice</li>
              <li>DND (Do-Not-Disturb) directives</li>
              <li>Anti-Spam regulations</li>
            </ul>
          </li>
          <li>Only subscribers who have <strong>permitted marketing communication</strong> will be contacted.</li>
          <li>All tele-marketing messages will clearly state: The service being offered, Pricing, Duration, How to subscribe, and How to opt out of further promotional messages.</li>
        </ul>
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <p className="text-blue-800 font-semibold">
            247 Comcare will never engage in unsolicited marketing or deceptive promotional practices.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "complaints",
    title: "3. Complaint Handling",
    icon: Target,
    content: (
      <>
        <p className="mb-6">
          247 Comcare is committed to providing effective, timely, and transparent complaint-resolution processes in line with the NCC Consumer Code of Practice Regulations. All complaints will be handled professionally and resolved within the timelines prescribed by the Commission.
        </p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">a. Complaint Processes</h4>
        <p className="mb-3">Subscribers may lodge complaints through any of the following channels:</p>
        
        <div className="bg-gradient-to-r from-blue-50 to-white p-5 rounded-xl mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📞</span>
              <div>
                <p className="font-semibold">Customer Care Hotline</p>
                <p className="text-blue-600">09161980940</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📧</span>
              <div>
                <p className="font-semibold">Email Support</p>
                <a href="mailto:hello@247comcare.com" className="text-blue-600 hover:underline">hello@247comcare.com</a>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-2xl">💬</span>
              <div>
                <p className="font-semibold">WhatsApp Support</p>
                <p className="text-blue-600">09161980940</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📍</span>
              <div>
                <p className="font-semibold">Physical Address</p>
                <p className="text-sm text-gray-600">Venia Place, Plot 8, Providence Street, Opposite Halifield School, Lekki Phase 1, Lagos, Nigeria.</p>
              </div>
            </div>
          </div>
        </div>

        <h5 className="font-semibold mb-3">On receipt of a complaint:</h5>
        <ol className="list-decimal pl-6 mb-8 space-y-2">
          <li><strong>Acknowledgment</strong> is provided immediately or within the same day.</li>
          <li><strong>Investigation</strong> begins promptly with details recorded.</li>
          <li><strong>Resolution</strong> is provided within NCC-specified timelines.</li>
          <li><strong>Feedback</strong> is communicated to the subscriber in clear, simple language.</li>
        </ol>

        <p className="text-sm text-gray-600 mb-8">
          Complaints may include issues related to billing, service access, delays in content delivery, or opt-in/opt-out confirmation errors.
        </p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">b. Further Recourse</h4>
        <p className="mb-3">If a subscriber is dissatisfied with the outcome of a complaint, they will be informed of additional escalation options:</p>
        <ol className="list-decimal pl-6 mb-8 space-y-2">
          <li>Internal escalation to a supervisor or senior customer service personnel</li>
          <li>Escalation to the Mobile Network Operator (MNO) or Aggregator</li>
          <li>Further escalation to the <strong>Nigerian Communications Commission (NCC)</strong>, in accordance with the Commission's Consumer Complaint/Dispute Resolution Framework</li>
        </ol>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">c. Data Collection and Analysis of Complaints</h4>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>247 Comcare will maintain a structured system for collecting, categorizing, and analyzing all complaints received.</li>
          <li>The data will track: Nature of complaints, Root cause, Time to resolution, Corrective actions taken, Frequency of repeat complaints</li>
          <li>The analysis will be used to identify trends, improve internal processes, and prevent recurring issues.</li>
        </ul>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">d. Retention of Records</h4>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>All complaint records including logs, resolutions, timestamps, and correspondence will be retained for a <strong>minimum of 24 months</strong> or as required by NCC regulations.</li>
          <li>Records will remain accessible for audit, regulatory review, or dispute resolution.</li>
          <li>Stored information will be handled in compliance with NDPR and NCC Data Protection Guidelines.</li>
        </ul>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">e. Prevention of Unsolicited SMS</h4>
        <p className="mb-3">247 Comcare is committed to eliminating unsolicited communications. Measures include:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>Strict enforcement of <strong>Double Opt-In (DOI)</strong> before any service activation</li>
          <li>No marketing or promotional SMS to users on the <strong>Do-Not-Disturb (DND)</strong> list</li>
          <li>Ensuring all marketing campaigns are permission-based</li>
          <li>Clear "STOP" instructions included in promotional SMS where applicable</li>
          <li>Regular audits with aggregators and MNOs to ensure adherence to anti-spam guidelines</li>
        </ul>
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <p className="text-red-800 font-semibold text-center">
            247 Comcare will not subscribe, bill, or send promotional content to any subscriber without explicit consent.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "obligations",
    title: "4. Consumer Obligations",
    icon: User,
    content: (
      <>
        <p className="mb-4">
          To ensure a smooth experience across all 247 Comcare services, consumers are expected to:
        </p>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-blue-500 mr-3 mt-1">•</span>
            <span>Provide accurate information when subscribing to any service.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3 mt-1">•</span>
            <span>Use services responsibly and in accordance with Nigerian laws and NCC regulations.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3 mt-1">•</span>
            <span>Safeguard their mobile devices and SIM cards to prevent unauthorized access or misuse.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3 mt-1">•</span>
            <span>Review all subscription messages, including pricing, duration, and confirmation prompts, before opting in.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3 mt-1">•</span>
            <span>Keep their contact information updated to ensure proper receipt of service notifications.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3 mt-1">•</span>
            <span>Follow provided opt-out procedures when they wish to discontinue a service.</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-500 mr-3 mt-1">•</span>
            <span>Report any suspected unauthorized subscription, billing error, or service issue promptly via approved customer care channels.</span>
          </li>
        </ul>
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <p className="text-blue-800">
            Consumers are encouraged to actively review their SMS inbox and transaction notifications to stay aware of their subscription status.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "protection",
    title: "5. Protection of Consumer Information",
    icon: ShieldCheck,
    content: (
      <>
        <p className="mb-6">
          247 Comcare is committed to safeguarding consumer data in compliance with the Nigerian Data Protection Regulation (NDPR) and NCC Data Protection Guidelines. All consumer information is handled lawfully, transparently, and securely.
        </p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">a. Data Gathering</h4>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>Consumer data is collected <strong>only</strong> for lawful business purposes such as service delivery, billing, customer verification, complaint handling, and regulatory reporting.</li>
          <li>Information collected may include:
            <ul className="list-circle pl-6 mt-2 space-y-1">
              <li>MSISDN (phone number)</li>
              <li>Subscription timestamps</li>
              <li>Service preferences</li>
              <li>Complaint records</li>
              <li>User consent logs (DOI records)</li>
            </ul>
          </li>
          <li>No data is collected without subscriber knowledge or in violation of NCC consent requirements.</li>
        </ul>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">b. Data Storage</h4>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>All consumer information is stored in <strong>secure, access-controlled environments</strong> with encryption and industry-standard security safeguards.</li>
          <li>Access to data is restricted to authorized personnel strictly on a need-to-know basis.</li>
          <li>Data retention follows NCC and NDPR requirements.</li>
          <li>Data is stored only for as long as necessary to fulfill service obligations, comply with legal mandates, or resolve disputes.</li>
          <li>Adequate measures are in place to prevent:
            <ul className="list-circle pl-6 mt-2 space-y-1">
              <li>Unauthorized access</li>
              <li>Data loss</li>
              <li>Alteration</li>
              <li>Breach</li>
            </ul>
          </li>
        </ul>
        <p className="text-sm text-gray-600 mb-8">247 Comcare conducts periodic security reviews to maintain data integrity.</p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">c. Data Sharing</h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>Consumer data will <strong>not</strong> be sold, traded, or shared with unauthorized third parties.</li>
          <li>Data may be shared only under the following conditions:
            <ol className="list-decimal pl-6 mt-2 space-y-1">
              <li><strong>With Mobile Network Operators (MNOs) or licensed Aggregators</strong> solely for service delivery and billing.</li>
              <li><strong>With regulators</strong> such as NCC when formally required for compliance or investigations.</li>
              <li><strong>With law enforcement agencies</strong> upon receipt of valid legal requests.</li>
              <li><strong>With service partners</strong> strictly when necessary to deliver a subscribed service and only under confidentiality and data protection agreements.</li>
            </ol>
          </li>
          <li>Any third party that receives consumer data must comply with NDPR, NCC guidelines, and equivalent security standards.</li>
        </ul>
      </>
    ),
  },
  {
    id: "billing",
    title: "6. Consumer Billing",
    icon: CreditCard,
    content: (
      <>
        <p className="mb-6">
          247 Comcare ensures that all billing practices are transparent, accurate, and fully compliant with NCC regulations. All charges are applied only after a subscriber has provided explicit consent through the Double Opt-In (DOI) process.
        </p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">a. Billing Information</h4>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>All billing information (price, duration, type of service, and charge type) will be clearly communicated <strong>before</strong> subscription.</li>
          <li>Billing information will appear in:
            <ul className="list-circle pl-6 mt-2 space-y-1">
              <li>DOI confirmation messages</li>
              <li>USSD/Web interfaces</li>
              <li>IVR prompts</li>
              <li>Welcome messages</li>
            </ul>
          </li>
          <li>All charges will be presented in Nigerian Naira (₦) and in straightforward, non-misleading language.</li>
        </ul>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">b. Timing for Issuance of Bill</h4>
        <p className="mb-3">247COMCARE shall process and issue bills within 10 days of the closure of each billing period. A bill shall include all charges incurred during the billing period except where:</p>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>Billing occurs immediately after the subscriber confirms the subscription via DOI except for services with free trials.</li>
          <li>For services that rely on mobile network billing (DCB), charges are processed instantly once authorization is completed.</li>
          <li>Subscribers receive confirmation of successful billing within seconds via SMS.</li>
          <li>No charge is applied before confirmation, and no charge is applied after the service duration expires unless the user initiates a new subscription.</li>
        </ul>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">c. Payment Confirmation</h4>
        <p className="mb-3">Immediately after billing, subscribers receive an SMS containing:</p>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>Confirmation of successful payment</li>
          <li>Name of service subscribed to</li>
          <li>Amount charged</li>
          <li>Duration of access</li>
          <li>Opt-out instructions (if applicable)</li>
        </ul>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">d. Billing Frequency</h4>
        <p className="mb-8">
          247COMCARE shall provide Customers with advance written notification of any proposed changes in billing periods, such advance notification to be at least equal to two (2) of its otherwise applicable billing periods (i.e. at least 2 months in advance where the billing period being changed is monthly).
        </p>

        <h4 className="text-xl font-semibold mb-4 text-blue-900">e. Prepaid Services</h4>
        <p className="mb-3">
          All 247 Comcare VAS products operate strictly as <strong>prepaid services</strong>, with charges applied only after the subscriber has confirmed interest.
        </p>
        <p className="mb-3">
          However, certain services may include a <strong>one-day free trial period</strong> to allow subscribers evaluate the service and decide if they wish to continue.
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>For services with a free trial, subscribers will be clearly informed during the DOI process that:
            <ol className="list-decimal pl-6 mt-2 space-y-1">
              <li>The first day is free</li>
              <li>The applicable charge will apply <strong>only after</strong> the free trial expires <strong>if the subscriber chooses to continue</strong></li>
            </ol>
          </li>
          <li>Outside of free-trial services, all subscriptions remain fully prepaid; subscribers are billed <strong>before accessing the service</strong>, not afterward.</li>
          <li>247 Comcare does <strong>not</strong> offer postpaid or credit-based billing for any VAS product.</li>
          <li>Subscribers are always informed of the exact service cost, duration, and terms <strong>before confirming</strong> the subscription.</li>
        </ul>
      </>
    ),
  },
];

function CodeOfPractice() {
  const [activeSection, setActiveSection] = useState("provision");

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7F6EE] to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1b1d22] to-[#24262b] text-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              Consumer Code of Practice
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              247 Comcare Nigeria Limited is committed to providing transparent, fair, and compliant services to all our subscribers.
            </p>
            <div className="mt-6 flex justify-center">
              <span className="inline-block w-20 h-1 bg-[#4B6EC1] rounded"></span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="sticky top-0 bg-white shadow-md z-10">
        <div className="container mx-auto px-4">
          <div className="overflow-x-auto scrollbar-hide flex justify-center">
            <div className="flex space-x-1 py-3">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap flex items-center ${
                    activeSection === section.id
                      ? "bg-[#4B6EC1] text-white shadow-lg"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <section.icon className="w-4 h-4 mr-2" />
                  {section.title.split(".")[0]}.
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-10 !mb-16">
        <div className="max-w-4xl mx-auto">
          {sections.map((section) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: activeSection === section.id ? 1 : 0,
                y: activeSection === section.id ? 0 : 20,
                display: activeSection === section.id ? "block" : "none",
              }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-white  shadow-lg p-6 md:p-10">
                <div className="flex items-center mb-6">
                  <section.icon className="w-10 h-10 mr-4 text-[#4B6EC1]" />
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1b1d22]">
                    {section.title}
                  </h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700">
                  {section.content}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CodeOfPractice;
